import {
  FUNNEL_BEFORE_FIRST_STAGE,
  type FunnelStageCounts,
} from "@repo/app-types/api/funnel";
import type { FunnelStages } from "@repo/app-types/entities/funnel";
import { useMemo, useState, type FC, type KeyboardEvent } from "react";
import {
  ResponsiveContainer,
  Sankey,
  type SankeyLinkProps,
  type SankeyNodeProps,
} from "recharts";
import { twMerge } from "tailwind-merge";
import {
  buildFunnelFlowData,
  funnelColorClassNames,
  formatFunnelShare,
  formatFunnelStageName,
  FunnelFlowNodeKind,
  type FunnelStageFocus,
} from "./funnelFormatting.js";

/** A stage key, or `FUNNEL_BEFORE_FIRST_STAGE` for null, so hover can tell nothing from the start node. */
function buildSelectionId(stageKey: string | null): string {
  return stageKey ?? FUNNEL_BEFORE_FIRST_STAGE;
}

function formatCount(count: number): string {
  return count.toLocaleString("en-US");
}

const NODE_WIDTH = 8;
/** Room between an exit and the next, for the count beside the active ones. */
const NODE_PADDING = 24;
const CHART_MARGIN = { top: 50, right: 112, bottom: 36, left: 8 };
/** A stage label's two 16px lines: its name, then its share. */
const LABEL_HEIGHT = 34;
/** Between a stage label and its node. */
const LABEL_OFFSET = 12;
/** Between a stage's label and the next column's. */
const LABEL_GAP = 12;

/** The number of columns after the first: the longest path from the start node. */
function findMaxDepth(links: readonly { source: number; target: number }[]) {
  const depths = new Map<number, number>([[0, 0]]);
  // Links are listed source by source in flow order, so one pass settles every depth.
  for (const link of links) {
    const depth = (depths.get(link.source) ?? 0) + 1;
    depths.set(link.target, Math.max(depths.get(link.target) ?? 0, depth));
  }
  return Math.max(...depths.values());
}

function checkActivationKey(event: KeyboardEvent): boolean {
  return event.key === "Enter" || event.key === " ";
}

export interface FunnelFlowChartProps {
  stages: FunnelStages;
  /** The agent cohort's. */
  stageCounts: FunnelStageCounts;
  selectedStageKey: FunnelStageFocus;
  onSelectedStageKeyChange: (stageKey: FunnelStageFocus) => void;
}

/**
 * The interactions' flow through the stages; a column, node or link focuses
 * its stage, and again goes back to every stage.
 */
export const FunnelFlowChart: FC<FunnelFlowChartProps> = (props) => {
  const { stages, stageCounts, selectedStageKey, onSelectedStageKeyChange } =
    props;
  const { denominator } = stageCounts;
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [chartWidth, setChartWidth] = useState(0);
  const selectedId =
    selectedStageKey === undefined
      ? undefined
      : buildSelectionId(selectedStageKey);
  // The selected stage is drawn active, else the hovered one, else every one.
  const activeId = selectedId ?? hoveredId ?? undefined;
  const checkActive = (id: string) => activeId === undefined || id === activeId;
  const toggleStage = (stageKey: string | null) =>
    onSelectedStageKeyChange(
      buildSelectionId(stageKey) === selectedId ? undefined : stageKey,
    );

  const chartData = useMemo(
    () => buildFunnelFlowData(stages, stageCounts),
    [stages, stageCounts],
  );

  // Recharts spaces the columns evenly, so each stage label gets one column.
  const maxDepth = findMaxDepth(chartData.links);
  const columnWidth =
    (chartWidth - CHART_MARGIN.left - CHART_MARGIN.right - NODE_WIDTH) /
    Math.max(maxDepth, 1);
  const labelWidth = Math.max(0, columnWidth - LABEL_GAP);
  // The start node and the stages come first, so this is the last stage's.
  const lastColumnIndex = chartData.nodes.findLastIndex(
    (node) => node.kind !== FunnelFlowNodeKind.Exit,
  );

  if (denominator === 0) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        No labeled interactions yet
      </div>
    );
  }

  const renderNode = (nodeProps: SankeyNodeProps) => {
    const { x, y, width, height, index } = nodeProps;
    const node = chartData.nodes[index];
    if (!node) {
      return <g />;
    }
    const isExit = node.kind === FunnelFlowNodeKind.Exit;
    const nodeId = buildSelectionId(node.stageKey);
    const isActive = checkActive(nodeId);
    const share = formatFunnelShare(node.value / denominator);
    const description = isExit
      ? `${formatFunnelStageName(stages, node.stageKey)}, went no further: ${node.name}, ${formatCount(node.value)} interactions (${share})`
      : `${node.name}: ${formatCount(node.value)} interactions (${share})`;
    const activateNode = () => toggleStage(node.stageKey);

    return (
      <g
        role="button"
        tabIndex={0}
        aria-label={description}
        aria-pressed={nodeId === selectedId}
        className="group cursor-pointer outline-none"
        onClick={activateNode}
        onMouseEnter={() => setHoveredId(nodeId)}
        onMouseLeave={() => setHoveredId(null)}
        onKeyDown={(event) => {
          if (checkActivationKey(event)) {
            event.preventDefault();
            activateNode();
          }
        }}
      >
        <title>{description}</title>
        {!isExit && (
          // The whole column, label and links included, up to the next
          // stage's node. Exits are drawn after the stages, so they stay on
          // top of the column they end in.
          <rect
            x={x}
            y={0}
            width={
              index === lastColumnIndex
                ? Math.max(0, chartWidth - x)
                : columnWidth
            }
            height="100%"
            fill="transparent"
          />
        )}
        <rect
          x={x - 7}
          y={y - 4}
          width={width + 14}
          height={height + 8}
          fill="transparent"
          className="group-focus-visible:stroke-primary"
          strokeWidth={2}
        />
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          className={twMerge(
            "transition-opacity",
            funnelColorClassNames[node.color].fill,
          )}
          opacity={isActive ? 1 : hoveredId === nodeId ? 0.4 : 0.2}
        />
        {isExit ? (
          isActive || hoveredId === nodeId ? (
            <text
              x={x + width + 6}
              y={y + height / 2}
              dominantBaseline="central"
              className="pointer-events-none fill-muted-foreground text-xs tabular-nums"
            >
              {formatCount(node.value)}
            </text>
          ) : null
        ) : (
          // The name, and below it the share, on 16px lines ending just
          // above the node.
          <foreignObject
            x={x}
            y={y - LABEL_HEIGHT - LABEL_OFFSET}
            width={labelWidth}
            height={LABEL_HEIGHT}
          >
            <div className="flex flex-col gap-0.5" title={node.name}>
              <span className="truncate text-xs leading-4 font-medium text-foreground">
                {node.name}
              </span>
              {node.kind === FunnelFlowNodeKind.Stage && (
                <span className="text-xs text-muted-foreground tabular-nums">
                  {share}
                </span>
              )}
            </div>
          </foreignObject>
        )}
      </g>
    );
  };

  const renderLink = (linkProps: SankeyLinkProps) => {
    const {
      sourceX,
      sourceY,
      targetX,
      targetY,
      sourceControlX,
      targetControlX,
      linkWidth,
      index,
    } = linkProps;
    const link = chartData.links[index];
    const sourceNode = link ? chartData.nodes[link.source] : undefined;
    const targetNode = link ? chartData.nodes[link.target] : undefined;
    if (!link || !sourceNode || !targetNode) {
      return <path />;
    }
    const description = `${sourceNode.name}: ${link.isExit ? `Went no further, ${targetNode.name}` : "Continued"}, ${formatCount(link.value)} interactions`;
    const linkId = buildSelectionId(link.stageKey);
    const isActive = checkActive(linkId);
    const isHovered = linkId === hoveredId;
    const activateLink = () => toggleStage(link.stageKey);

    // Its stage's column, drawn over it, takes the pointer; the link is a
    // keyboard target.
    return (
      <g>
        <path
          d={`M${sourceX},${sourceY} C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}`}
          fill="none"
          className={twMerge(
            "transition-[stroke-opacity] outline-none focus-visible:[stroke-opacity:1]",
            funnelColorClassNames[link.color].stroke,
          )}
          strokeWidth={linkWidth}
          strokeOpacity={
            link.isExit
              ? isActive
                ? 0.95
                : isHovered
                  ? 0.3
                  : 0.16
              : isActive
                ? 0.85
                : isHovered
                  ? 0.12
                  : 0.05
          }
          role="button"
          tabIndex={0}
          aria-label={description}
          aria-pressed={linkId === selectedId}
          onKeyDown={(event) => {
            if (checkActivationKey(event)) {
              event.preventDefault();
              activateLink();
            }
          }}
        />
        {!link.isExit && (
          <text
            x={(sourceX + targetX) / 2}
            y={(sourceY + targetY) / 2}
            textAnchor="middle"
            dominantBaseline="central"
            className={twMerge(
              "pointer-events-none text-xs tabular-nums",
              isActive ? "fill-foreground" : "fill-muted-foreground",
            )}
          >
            {formatCount(link.value)}
          </text>
        )}
      </g>
    );
  };

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <div className="h-[400px] min-w-[880px]">
          <ResponsiveContainer
            width="100%"
            height="100%"
            onResize={(width) => setChartWidth(width)}
          >
            <Sankey
              data={chartData}
              align="left"
              verticalAlign="top"
              sort={false}
              iterations={0}
              nodeWidth={NODE_WIDTH}
              nodePadding={NODE_PADDING}
              margin={CHART_MARGIN}
              node={renderNode}
              link={renderLink}
            />
          </ResponsiveContainer>
        </div>
      </div>
      <ul
        className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground"
        aria-label="Flow colors"
      >
        {chartData.legend.map((entry) => (
          <li
            key={`${entry.color}:${entry.label}`}
            className="inline-flex items-center gap-2"
          >
            <span
              className={twMerge(
                "size-2 rounded-full",
                funnelColorClassNames[entry.color].background,
              )}
            />
            {entry.label}
          </li>
        ))}
      </ul>
    </div>
  );
};
